import PropTypes from "prop-types"
// @mui
import { Box, Tooltip } from "@mui/material"
import { alpha, lighten, useTheme } from "@mui/material/styles"
// hooks
import useLocales from "minimals-template/components/@hooks/useLocales"
// guards
import RoleBasedGuard from "minimals-template/components/guards/RoleBasedGuard"
//
import { Else, If } from "lib/@components/switch"
import { makeId } from "lib/make-id"
import { resolveValue } from "lib/resolve-as-function"
import { RouterLink } from "lib/routes/router-link"
import { isExternalLink } from "./nav-helpers"
import { navTooltipSlotProps } from "./nav-font"
import Iconify from "minimals-template/components/Iconify"
import { ListItemIconStyle, ListItemStyle, ListItemTextStyle } from "./style"

// ----------------------------------------------------------------------

function getTestId(title) {
    if (typeof title !== "string") return {}
    return {
        "data-cy": `navItem-${makeId(title)}`,
    }
}

export default function NavItem({ item, depth, active, open, isCollapse, ...other }) {
    const { translate } = useLocales()
    const theme = useTheme()

    const { title, icon, info, children, disabled, caption, roles } = item
    const path = resolveValue(item.path)
    const linkProps = getLinkProps()
    const isExpandable = !!children && !isCollapse

    const renderContent = (
        <ListItemStyle
            {...getTestId(title)}
            depth={depth}
            active={active}
            aria-current={active ? "page" : undefined}
            aria-expanded={isExpandable ? open : undefined}
            disabled={disabled}
            isCollapse={isCollapse}
            {...linkProps}
            {...other}
        >
            {icon && <ListItemIconStyle isCollapse={isCollapse}>{icon}</ListItemIconStyle>}

            {depth !== 1 && <DotIcon active={active && depth !== 1} />}

            <ListItemTextStyle
                isCollapse={isCollapse}
                primary={translate(title)}
                secondary={
                    caption && (
                        <Tooltip title={translate(caption)} placement="top-start" slotProps={navTooltipSlotProps}>
                            <span>{translate(caption)}</span>
                        </Tooltip>
                    )
                }
                primaryTypographyProps={{
                    component: "span",
                    noWrap: true,
                    variant: active ? "subtitle2" : "body2",
                }}
                secondaryTypographyProps={{
                    noWrap: true,
                    variant: "caption",
                }}
            />

            {!isCollapse && (
                <>
                    {info && (
                        <Box
                            component="span"
                            sx={{
                                lineHeight: 0,
                                mr: 1,
                                ...(active && {
                                    "& > *": {
                                        boxShadow: `0 0 0 2px ${
                                            theme.palette.mode === "dark"
                                                ? alpha(theme.palette.primary.light, 0.32)
                                                : lighten(theme.palette.primary.lighter, 0.3)
                                        }`,
                                    },
                                }),
                            }}
                        >
                            {info}
                        </Box>
                    )}

                    {!!children && (
                        <Iconify
                            icon={open ? "eva:arrow-ios-downward-fill" : "eva:arrow-ios-forward-fill"}
                            sx={{ width: 16, height: 16, ml: 1, flexShrink: 0 }}
                        />
                    )}
                </>
            )}
        </ListItemStyle>
    )

    return (
        <RoleBasedGuard roles={roles}>
            <If value={isCollapse}>
                <Tooltip title={translate(title)} placement="right" arrow slotProps={navTooltipSlotProps}>
                    {renderContent}
                </Tooltip>
                <Else>{renderContent}</Else>
            </If>
        </RoleBasedGuard>
    )

    function getLinkProps() {
        if (!path) {
            return {}
        }
        if (isExternalLink(path)) {
            return { component: "a", href: path, target: "_blank", rel: "noopener" }
        }
        return { component: RouterLink, to: path }
    }
}

// ----------------------------------------------------------------------

DotIcon.propTypes = {
    active: PropTypes.bool,
}

export function DotIcon({ active }) {
    return (
        <ListItemIconStyle>
            <Box
                component="span"
                sx={{
                    width: 4,
                    height: 4,
                    borderRadius: "50%",
                    bgcolor: "text.disabled",
                    transition: (theme) =>
                        theme.transitions.create("transform", {
                            duration: theme.transitions.duration.shorter,
                        }),
                    ...(active && {
                        transform: "scale(2)",
                        bgcolor: "primary.main",
                    }),
                }}
            />
        </ListItemIconStyle>
    )
}
